import { getLocationType } from "./location.service";
import { formatCustomerType, formatUserPosition } from "./format.service";
/**
 * Populates the service details from a quote and vehicle.
 *
 * @param {Object} quote - The quote object containing service, pricing, and supplier details.
 * @param {Object} vehicle - The vehicle object containing vehicle parameters and information.
 * @returns {Object|null} - The populated service details or null if no service is found.
 */
export function populateService(supplier, service, pricing, surcharges = []) {
    if (supplier && service) {
        const { vehicle } = service;
        return {
            _id: service._id,
            vehicle: {
                _id: vehicle._id,
                seats: vehicle.parameters.n_seats,
                suitcases: vehicle.parameters.n_suitcases,
                type_service: vehicle.parameters.type_category,
                category: vehicle.parameters.category,
                make: vehicle.parameters.make,
                make_label: vehicle.parameters.name,
                image: vehicle.information.image,
                isComposite: vehicle.parameters.isComposite
            },
            hours_advance: service.advancedReservation,
            preference: service.preference,
            currency: {
                code: service.currency_code,
                symbol: service.currency_symbol,
                label: service.currency_label
            },
            branchId: service.branch,
            supplierId: supplier ? supplier._id : null,
            base_price: pricing.base_price.last_amount,
            sale_price: pricing.sale_price.last_amount,
            basePriceComposition: pricing.base_price.composition,
            surcharges: surcharges,
            unit: supplier ? supplier.settings.measurementUnit : "km",
            distance_include: service.hours_disposition
                ? service.hours_disposition.km_include
                : 0,
            min_hours: service.hours_disposition
                ? service.hours_disposition.min_hours
                : 0,
            cancellationPolicy: service.cancellationPolicy
                ? service.cancellationPolicy.descriptions
                : [],
            keyDetails: service.keyDetails,
            termsConditions: service.termsConditions
        };
    }
    return null;
};

/**
 * Populates a location object for use in selection components.
 *
 * @param {Object} location - The location object containing country, coordinates, metadata, and place ID.
 * @param {String} transferType - The type of transfer (e.g., "one-way", "round-trip").
 * @returns {Array} - An array containing the formatted location object or an empty array if no location is provided.
 */
export function populateLocation(location, transferType, locationType) {
    if (location) {
        return [
            {
                select: location.name,
                country_code: location.country,
                location: {
                    lng: location.lng,
                    lat: location.lat
                },
                owner: "",
                place_id: location.googlePlaceId,
                type: getLocationType(transferType, locationType),
                metadata: location.metadata,
            }
        ];
    }
    return [];
}

/** * Populates client details from a client object.
 *
 * @param {Object} client - The client object containing personal and company information.
 * @returns {Object|null} - The populated client details or null if no client is provided.
 */
export function populateClient(client) {
    if (client) {
        return {
            _id: client._id,
            first_name: client.first_name,
            last_name: client.last_name,
            company_name: client.company_name,
            mark_up: client.mark_up,
            type_markup: client.type_markup,
            commission: client.commission,
            commercialTerms: client.type_charge,
            email: client.email,
            phone: client.phone,
            type: client.type,
        }
    }
    return null;
}

/** * Populates branch options from a branch object.
 *
 * @param {Object} branch - The branch object containing branch details.
 * @returns {Array} - An array containing the formatted branch object or an empty array if no branch is provided.
 */
export function populateBranchOptions(branch) {
    if (branch) {
        return [
            {
                _id: branch._id,
                name: branch.name,
                timezone: branch.timezone,
                supplier: branch.supplier,
            }
        ];
    }
    return [];
}

/** * Populates supplier options from a supplier object.
 *
 * @param {Object} supplier - The supplier object containing business details, settings, and notes.
 * @returns {Array} - An array containing the formatted supplier object or an empty array if no supplier is provided.
 */
export function populateSupplierOptions(supplier) {
    if (supplier) {
        return [
            {
                _id: supplier._id,
                businessDetails: supplier.businessDetails,
                settings: supplier.settings,
                notes: supplier.notes,
            }
        ];
    }
    return [];
}
/** * Populates responsible user details based on user data and type.
 *
 * @param {Object} userData - The user data object containing personal and agreement information.
 * @param {String} userType - The type of user (e.g., "Users", "Operators", "Customers").
 * @returns {Object|null} - The populated responsible user details or null if no valid user data/type is provided.
 */
export function populateResponsibleUser(userData, userType) {
    if (userData && userType) {
        if (userType === 'Users') {
            return {
                fullName: `${userData.personal_info.first_name} ${userData.personal_info.last_name}`,
                position: formatUserPosition(userData.agreement.position),
                type: userData.relationship.type
            };
        } else if (userType === 'Operators') {
            return {
                fullName: `${userData.contactPerson.firstName} ${userData.contactPerson.lastName}`,
                position: "Supplier",
                type: "supplier"
            };
        } else if (userType === 'Customers') {
            return {
                fullName: `${userData.first_name} ${userData.last_name}`,
                position: formatCustomerType(userData.type),
                type: userData.type
            };
        }
        return null;
    }
}